import React, { useState, useEffect } from 'react'

import LoadingPlaceholder from 'components/LoadingPlaceholder'
import { Flex } from 'components/Box'
import InfiniteList, { handleFetchMore } from 'components/InfiniteList'
import EmptyState from 'modules/shares/components/EmptyState'

import { Layout } from '../components/Layout'
import {
  ListGrid,
  ListGridItem,
  ItemImage,
  ItemName,
  Group,
  ItemImageWrapper,
  GroupName,
} from '../components/Grid'

import { groupStyles } from '../utils'
import { useGetLayerStyles } from '../operations'
import useResponsiveImage from '../useResponsiveImage'
import { useSearchComponentsContext } from '../SearchCwvContext'

// This is a valid use case to validate TextStyle
// eslint-disable-next-line no-restricted-imports
import { LayerStyle } from 'gqlTypes/expansiveTypes'
import type { File } from '../types'

const LayerItem = ({ layerStyle }: { layerStyle: LayerStyle }) => {
  const imageProps = useResponsiveImage({
    name: layerStyle.name,
    files: layerStyle.files as File[],
  })

  return (
    <ListGridItem>
      <ItemImageWrapper>
        <ItemImage {...imageProps} />
      </ItemImageWrapper>
      <ItemName>{layerStyle.name}</ItemName>
    </ListGridItem>
  )
}

const LayerList = ({
  searchCwv,
  path,
}: {
  searchCwv: string
  path: string | null
}) => {
  const { entries, meta, fetchMore, loading, filter } = useGetLayerStyles(
    searchCwv
  )

  useEffect(() => {
    filter(path === 'View All' ? null : path)
    // we do NOT want to add filter as a dependency here
    // eslint-disable-next-line
  }, [path])

  if (loading) {
    return (
      <Flex justifyContent="center" flex="auto">
        <LoadingPlaceholder size="64px" />
      </Flex>
    )
  }

  const layerGroups = groupStyles(entries)
  const groupsNames = Object.keys(layerGroups)

  if (groupsNames.length === 0) {
    if (searchCwv !== '') {
      return (
        <EmptyState
          title="No search results"
          description="No matching items."
          icon="search"
        />
      )
    }

    return (
      <EmptyState
        title="No Layer Styles"
        description="This Document does not contain any Layer Styles."
        icon="layerStyle"
      />
    )
  }

  return (
    <InfiniteList
      onLoadMore={handleFetchMore(
        fetchMore,
        ['share', 'version', 'document', 'components', 'entries'],
        { after: meta?.after || null }
      )}>
      {groupsNames.map(groupName => (
        <Group key={groupName}>
          <GroupName groups={groupName} />
          <ListGrid>
            {layerGroups[groupName].map((layerStyle: LayerStyle) => (
              <LayerItem key={layerStyle.identifier} layerStyle={layerStyle} />
            ))}
          </ListGrid>
        </Group>
      ))}
    </InfiniteList>
  )
}

export const LayerStyles: React.FC = () => {
  const { searchCwv } = useSearchComponentsContext()
  const [selected, setSelected] = useState('View All')

  return (
    <Layout onSelect={setSelected} type="LAYER_STYLE" selected={selected}>
      <LayerList searchCwv={searchCwv} path={selected} />
    </Layout>
  )
}
